import React, { useState, useEffect } from "react";
import { CommentDto, CommentLikeStatus } from "../../types/comment";
import LikeButton from "../LikeButton";
import { toggleCommentLike } from "../../services/commentService";
import { fetchAvatar } from "../../services/userService";
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { TiUserOutline } from "react-icons/ti";

dayjs.extend(relativeTime);

interface CommentItemProps {
  comment: CommentDto;
  onLikeToggle: (commentId: number, newStatus: CommentLikeStatus) => void;
}

const CommentItem: React.FC<CommentItemProps> = ({ comment, onLikeToggle }) => {
  const [expanded, setExpanded] = useState(false);
  const [avatar, setAvatar] = useState<string | null>(null);

  const handleLikeAction = async () => {
    const res = await toggleCommentLike(comment.commentId);

    if (res.success && res.resultObj) {
      const { liked, likeCount } = res.resultObj;

      onLikeToggle(comment.commentId, { liked, likeCount });

      return { liked, likeCount };
    } else {
      console.error("Failed to toggle like:", res.errors);
      return { liked: comment.isLikedByCurrentUser, likeCount: comment.likeCount };
    }
  };

  const getAvatar = async (userId: number) => {
    const response = await fetchAvatar(userId);
    setAvatar(response.avatar);
  };

  useEffect(() => {
    if (comment.userId) {
      getAvatar(comment.userId);
    }
  }, []);

  const fullText = comment.content;
  const shortText = comment.content.slice(0, 70);

  return (
    <div>
      <div className="flex gap-2 p-2">
        <div className="flex flex-col items-center">
          {avatar &&
            <img
              className="w-8 h-8 rounded-full mb-1 border border-gray-400 shadow-sm"
              src={avatar}
              alt="user avatar"
              onError={() => comment.userId ?? getAvatar(comment.userId)}
            />
          }
          {!avatar &&
            <TiUserOutline className="w-8 h-8 text-white rounded-full mb-1 border border-gray-400 shadow-sm" />
          }
          <div className="w-px flex-1 bg-white py-1"></div>
        </div>

        <div className="flex-1">
          <span className="text-lg text-accentAlt font-bold">{comment.userName}</span>
          <p className="text-sm text-primary font-normal mt-1 leading-relaxed text-left whitespace-pre-wrap">
            {expanded || fullText.length <= 70 ? fullText : shortText + "..."}
            {fullText.length > 70 && (
              <button
                onClick={() => setExpanded(!expanded)}
                className="inline text-primary font-medium ml-1 hover:underline hover:text-accentAlt underline-offset-2 transition-all duration-150"
              >
                {expanded ? "less" : "more"}
              </button>
            )}
          </p>
          <div className="flex justify-between items-center text-sm text-gray-500 mt-1">
            <LikeButton
              photoId={comment.commentId}
              initialLiked={comment.isLikedByCurrentUser}
              initialLikeCount={comment.likeCount}
              onLikeAction={handleLikeAction}
            />
            <span className="text-sm text-primary font-normal">
              {dayjs(comment.dtCreate).fromNow()}
            </span>
          </div>
        </div>
      </div>
      <div className="h-px bg-white/50 my-2"></div>
    </div>
  );
};

export default CommentItem;
